import { instrumentQueryKeys } from '@/config/queryKeys';
import { createInstrument, updateInstrument } from '@/services/instruments';
import type { InstrumentCreateRequest, InstrumentPublic, InstrumentUpdateRequest } from '@/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useRef } from 'react';
import { toast } from 'sonner';

interface UseInstrumentMutationsOptions {
  orgId: string;
  instrument?: InstrumentPublic;
  onSuccess?: () => void;
  t: TFunction<'instruments'>;
}

/**
 * Custom hook for instrument API mutations with optimized error/success handling
 */
export const useInstrumentMutations = (options: UseInstrumentMutationsOptions) => {
  const { orgId, instrument, onSuccess, t } = options;
  const queryClient = useQueryClient();
  const toastIdRef = useRef<string | number | undefined>(undefined);

  const invalidateList = async () => {
    await queryClient.invalidateQueries({ queryKey: instrumentQueryKeys.list(orgId) });
  };

  const createMutation = useMutation({
    mutationFn: (body: InstrumentCreateRequest) => createInstrument(orgId, body),
    onSuccess: async result => {
      await invalidateList();
      toast.success(t('create_success', { name: result.name }), { id: toastIdRef.current });
      toastIdRef.current = undefined;
      onSuccess?.();
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : String(err);
      console.error('[createInstrument]', message);
      toast.error(t('create_error_detail', { error: message }), { id: toastIdRef.current });
      toastIdRef.current = undefined;
    },
  });

  const updateMutation = useMutation({
    mutationFn: (body: InstrumentUpdateRequest) => {
      if (!instrument) throw new Error('update called without instrument');
      return updateInstrument(orgId, instrument.id, body);
    },
    onSuccess: async result => {
      await invalidateList();
      toast.success(t('update_success', { name: result.name }), { id: toastIdRef.current });
      toastIdRef.current = undefined;
      onSuccess?.();
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : String(err);
      console.error('[updateInstrument]', message);
      toast.error(t('update_error_detail', { error: message }), { id: toastIdRef.current });
      toastIdRef.current = undefined;
    },
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  // Helper to show loading toast and track its ID
  const showLoadingToast = (message: string) => {
    toastIdRef.current = toast.loading(message);
  };

  return {
    createMutation,
    updateMutation,
    showLoadingToast,
    isPending,
  } as const;
};
